
import { type EnhancedResults } from './types';
import { calculateScenarioValues, calculateAverageProbabilityOfSuccess } from './scenarioCalculations';

export interface ResultsBuilderParams {
  deepLearningResults: any;
  peerAnalysis: any;
  phaseAnalyses: Record<string, any>;
  marketIntelligence: any;
  hasBalanceSheetData: boolean;
  hasAssumptions: boolean;
  historicalCashFlow: number[];
  dlConfig: any;
  phases: readonly string[];
}

export function buildEnhancedResults(params: ResultsBuilderParams): EnhancedResults {
  const {
    deepLearningResults,
    peerAnalysis,
    phaseAnalyses,
    marketIntelligence,
    hasBalanceSheetData,
    hasAssumptions,
    historicalCashFlow,
    dlConfig,
    phases
  } = params;

  const scenarioValues = calculateScenarioValues(deepLearningResults.scenarios);
  const avgProbabilityOfSuccess = calculateAverageProbabilityOfSuccess(phaseAnalyses);

  // Enhanced scenarios object with worst case
  const enhancedScenarios = {
    ...deepLearningResults.scenarios,
    worstCase: scenarioValues.worstCaseScenario
  };

  return {
    deepLearningForecast: {
      forecast: deepLearningResults.forecast,
      confidence: deepLearningResults.confidence,
      modelAccuracy: deepLearningResults.modelAccuracy,
      featureImportance: deepLearningResults.featureImportance,
      scenarios: enhancedScenarios
    },
    peerComparison: {
      directComparators: peerAnalysis.directComparators,
      industryBenchmarks: peerAnalysis.industryBenchmarks,
      valuationMultiples: peerAnalysis.valuationMultiples,
      marketPositioning: peerAnalysis.marketPositioning,
      competitiveAdvantages: peerAnalysis.competitiveAdvantages,
      risks: peerAnalysis.risks
    },
    phaseAnalysis: phaseAnalyses,
    marketIntelligence,
    enhancedMetrics: {
      riskAdjustedNPV: scenarioValues.realisticScenarioValue,
      probabilityOfSuccess: avgProbabilityOfSuccess,
      valuationRange: {
        low: scenarioValues.worstCaseScenarioValue,
        base: scenarioValues.realisticScenarioValue,
        high: scenarioValues.optimisticScenarioValue,
        worstCase: scenarioValues.worstCaseScenarioValue
      },
      confidenceScore: Math.round(deepLearningResults.modelAccuracy * 100),
      dataQuality: hasBalanceSheetData && hasAssumptions ? 'Excellent' : 
                  hasBalanceSheetData || hasAssumptions ? 'Good' : 'Limited'
    },
    aiInsights: {
      keyFindings: [
        `Model accuracy: ${Math.round(deepLearningResults.modelAccuracy * 100)}%`,
        `Most important factor: ${deepLearningResults.featureImportance[0]?.feature || 'Trend analysis'}`,
        `Market positioning: ${peerAnalysis.marketPositioning.percentile}th percentile`,
        `Recommended phase focus: ${phases[0]}`,
        `Worst case scenario: ${scenarioValues.worstCaseScenarioValue < 0 ? 'Negative' : 'Positive'} value projection`
      ],
      riskFactors: [
        ...peerAnalysis.risks,
        'Worst case scenario shows significant downside risk'
      ],
      opportunities: peerAnalysis.competitiveAdvantages,
      recommendations: [
        'Focus on geological risk mitigation in early phases',
        'Leverage superior grade potential for valuation',
        'Consider strategic partnerships for development phase',
        'Implement ESG best practices for stakeholder confidence',
        'Prepare contingency plans for worst case scenarios'
      ]
    },
    modelDetails: {
      algorithmsUsed: ['LSTM Neural Networks', 'Transformer Models', 'Ensemble Methods'],
      dataPoints: historicalCashFlow.length,
      forecastHorizon: dlConfig.forecastHorizon,
      confidenceLevel: dlConfig.confidenceLevel,
      industryBenchmarks: peerAnalysis.industryBenchmarks.length,
      peerComparators: peerAnalysis.directComparators.length
    },
    generatedAt: new Date().toISOString()
  };
}
